import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import {
  Calendar, Clock, User, MapPin,
  CheckCircle, XCircle, AlertCircle, Loader2
} from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

export default function PatientAppointments() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [appointments, setAppointments] = useState([]);

  useEffect(() => {
    fetchAppointments();
  }, []);

  const fetchAppointments = async () => {
    try {
      setLoading(true);
      const patientId = user?.patient_id;

      if (!patientId) {
        toast.error('No se encontró información del paciente');
        setLoading(false);
        return;
      }

      const response = await api.get(`/appointments/patient/${patientId}`);
      setAppointments(response.data || []);
    } catch (error) {
      console.error('Error fetching appointments:', error);
      toast.error('Error al cargar las citas');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('es-PE', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleTimeString('es-PE', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirmada':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'cancelada':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'completada':
        return <CheckCircle className="w-5 h-5 text-blue-500" />;
      default:
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmada':
        return 'bg-green-100 text-green-800';
      case 'cancelada':
        return 'bg-red-100 text-red-800';
      case 'completada':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'confirmada':
        return 'Confirmada';
      case 'cancelada':
        return 'Cancelada';
      case 'completada':
        return 'Completada';
      case 'pendiente':
        return 'Pendiente';
      default:
        return status;
    }
  };

  // Separar citas futuras y pasadas
  const now = new Date();
  const upcomingAppointments = appointments.filter(
    apt => new Date(apt.appointment_date) >= now && apt.status !== 'cancelada'
  );
  const pastAppointments = appointments.filter(
    apt => new Date(apt.appointment_date) < now || apt.status === 'cancelada'
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-4xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="appointments-title">
          Mis Citas
        </h1>
        <p className="text-muted-foreground mt-2">
          Consulta y gestiona tus citas médicas
        </p>
      </div>

      {/* Próximas Citas */}
      <div>
        <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Próximas Citas
        </h2>
        {upcomingAppointments.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {upcomingAppointments.map((appointment) => (
              <Card key={appointment.id} className="p-6" data-testid={`appointment-${appointment.id}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Calendar className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <p className="font-semibold capitalize">{formatDate(appointment.appointment_date)}</p>
                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                        <Clock className="w-4 h-4" />
                        <span>{formatTime(appointment.appointment_date)}</span>
                      </div>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(appointment.status)}`}>
                    {getStatusText(appointment.status)}
                  </span>
                </div>

                {appointment.reason && (
                  <div className="mb-3">
                    <p className="text-xs text-muted-foreground">Motivo</p>
                    <p className="text-sm">{appointment.reason}</p>
                  </div>
                )}

                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <User className="w-4 h-4" />
                  <span>Cita médica programada</span>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="p-6" data-testid="no-upcoming-appointments">
            <div className="text-center py-8">
              <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No tienes citas programadas</p>
              <p className="text-sm text-muted-foreground mt-2">
                Contacta con tu médico para agendar una cita
              </p>
            </div>
          </Card>
        )}
      </div>

      {/* Historial de Citas */}
      <div>
        <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Historial de Citas
        </h2>
        {pastAppointments.length > 0 ? (
          <Card className="overflow-hidden" data-testid="past-appointments-card">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Fecha
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Hora
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Motivo
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Estado
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {pastAppointments.map((appointment) => (
                    <tr key={appointment.id} className="hover:bg-muted/50">
                      <td className="px-4 py-3 text-sm">
                        {new Date(appointment.appointment_date).toLocaleDateString('es-PE')}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {formatTime(appointment.appointment_date)}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {appointment.reason || 'No especificado'}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(appointment.status)}`}>
                          {getStatusText(appointment.status)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        ) : (
          <Card className="p-6" data-testid="no-past-appointments">
            <p className="text-muted-foreground text-center py-4">
              No hay citas anteriores registradas
            </p>
          </Card>
        )}
      </div>
    </div>
  );
}
